import React, { useState } from "react";
import { CashIcon, ShieldCheckIcon, TargetIcon } from "./icons";

// The Cash Games metabar: three facts that are true of every QUSHi cash game.
//
// Client: "The metabar is important, and the icons i used are in Stitch. Make
// the metabar exactly like you see it - placement is between the subtitle and
// the Starting soon, Live now, and Completed tabs."
//
// Taken from the Stitch file: a three-column grid on a faint fill, hairlines
// above and below it and between the cells, each cell a 32px icon over a 9px
// bold uppercase teal label. It is a static strip — none of it varies by game —
// so it is rendered once above the tabs rather than repeated on every card.
//
// ── The icons ─────────────────────────────────────────────────────────────
// The client supplied their own artwork for these three and asked for it by
// name: "All the icon images I used in the metabar are next to the design. Use
// all these same images from my design." Those files are not in the repo yet —
// the design only reaches us as Stitch's own temporary CDN URLs, which cannot be
// linked from production.
//
// So each cell asks for the client's file first and falls back to the closest
// icon QUSHi already ships if it is not there. Dropping the three PNGs into
// /public under these names is the whole swap; no code changes with them. They
// are the client's assets and they are what should ship — the fallbacks are so
// the screen is not broken art in the meantime, and they are real product icons
// rather than invented placeholders.
const ITEMS = [
  {
    key: "free",
    label: "Free to enter",
    subline: "No purchase required",
    src: "/cash-game-free-to-enter.png",
    Fallback: CashIcon,
  },
  {
    key: "age",
    label: "18+ only",
    subline: "Verify your age",
    src: "/cash-game-18-plus.png",
    Fallback: ShieldCheckIcon,
  },
  {
    key: "skill",
    label: "Skill based",
    subline: "Not by chance",
    src: "/cash-game-skill-based.png",
    Fallback: TargetIcon,
  },
];

const MetaIcon = ({ src, Fallback, className = "h-8 w-8" }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return <Fallback className={className} />;
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      className={`${className} object-contain`}
      onError={() => setBroken(true)}
    />
  );
};

/**
 * This strip appears in two of the client's designs and they are set slightly
 * differently, so both are supported rather than one being made to stand in for
 * the other:
 *
 *   LANDING PAGE   `variant="benefits"` — a white band with a bottom hairline,
 *                  a little more vertical room, and a second line under each
 *                  label ("No purchase required" / "Verify your age" /
 *                  "Not by chance"). It sits directly under the title banner,
 *                  so a white ground is what continues the banner cleanly.
 *   VIEW ALL       `variant="meta"` (default) — a faint filled band with a
 *                  hairline top AND bottom, tighter, label only. It sits
 *                  between the subtitle and the tabs, where a fill is what
 *                  separates the two.
 *
 * The labels, the icons and the type are identical in both — only the ground,
 * the rules and the second line differ, which is exactly what the two Stitch
 * files show.
 */
//
// The desktop design (Stitch 4747) sets the `benefits` strip as a bordered card
// with `p-6`, a 40px icon and a 14px label — the phone's 32px/9px inside a
// 1050px column reads as a caption rather than a row of claims. That is scale,
// which is what the desktop file governs, so it is applied at md; the icons,
// the teal and the weights stay the mobile file's.
const VARIANTS = {
  meta: {
    frame: "border-y border-gray-200 bg-[#F8FAFA]",
    cell: "py-2",
    icon: "h-8 w-8",
    label: "text-[9px] tracking-tight",
    subline: "text-[9px]",
  },
  benefits: {
    // Client: "Remove the outer card container from the metadata bar… Keep the
    // existing vertical dividers within the metadata bar and add a horizontal
    // divider directly beneath it. this should look just like it does on
    // mobile." The `border-b` IS that horizontal divider and the cells' own
    // `border-r` are the vertical ones — both were already here for the phone;
    // what has gone is the desktop-only card the landing page used to wrap this
    // in (rounded corners, a full border and a top margin, at the call site).
    //
    // `md:py-5` in place of `md:p-6`: the strip is flush to the page edges now,
    // like the phone's, so horizontal padding would pull the dividers in off
    // the banner's edge above it. Only the vertical room grows.
    // DESKTOP, from the client's new landing design: the strip becomes a
    // container card again ("The container cards should match the exact
    // container cards, shadows, etc., we use for league play landing pages"),
    // and each cell turns on its side — the icon to the LEFT of a two-line
    // block rather than above a centred one.
    //
    // That supersedes the earlier "Remove the outer card container from the
    // metadata bar… this should look just like it does on mobile", which was an
    // instruction about the PREVIOUS desktop page. The client's verdict on that
    // page is "The current desktop landing page is super weak. We have to change
    // it. Follow this design, please."
    //
    // Gated at `lg`, not `md`: three horizontal cells need ~180px each, and at
    // 768px the left column of the new two-column grid is only ~400px wide. Below
    // lg the phone's centred stack is what fits, and it is what the phone ships.
    //
    // The ICONS are ours, not Stitch's — client: "For the metabar, use our
    // current icons; Stitch is just the visual layout and text copy." So the
    // client's own PNGs render at their own size, with none of the design's
    // green-tinted glyph box behind them: that box is drawn for a monochrome
    // stroke icon and would tint the artwork sitting in it.
    frame: "border-b border-gray-200 bg-white md:rounded-2xl md:border md:px-2 md:py-4",
    cell: "py-3 md:py-4 lg:flex-row lg:items-center lg:gap-2.5 lg:px-3 lg:text-left",
    icon: "h-8 w-8 md:h-10 md:w-10",
    // `lg:text-xs` pulls the label back down when the cells turn horizontal.
    // md:text-sm is sized for a full-width strip; inside the two-column grid the
    // left column is ~550px, so a 14px label broke "FREE TO ENTER" over two
    // lines while the cell beside it sat on one — which is not a row of claims,
    // it is a ragged list.
    label:
      "text-[9px] tracking-tight md:mt-2 md:text-sm md:tracking-wider lg:mt-0 lg:whitespace-nowrap lg:text-xs lg:leading-tight lg:tracking-wide",
    subline: "text-[9px] md:mt-0.5 md:text-xs lg:whitespace-nowrap lg:text-[11px]",
  },
};

const CashGameMetaBar = ({ className = "", variant = "meta" }) => {
  const v = VARIANTS[variant] || VARIANTS.meta;
  const withSubline = variant === "benefits";
  return (
    <div className={`grid grid-cols-3 ${v.frame} ${className}`}>
      {ITEMS.map((item, index) => (
        <div
          key={item.key}
          className={`flex flex-col items-center justify-center px-1 text-center ${v.cell} ${
            index < ITEMS.length - 1 ? "border-r border-gray-200" : ""
          }`}
        >
          <MetaIcon
            src={item.src}
            Fallback={item.Fallback}
            className={`${v.icon} lg:shrink-0`}
          />
          {/* One wrapper so the label and its subline travel together when the
              cell turns horizontal — without it the icon, the label and the
              subline become three siblings in a row. */}
          <span className="min-w-0 lg:flex lg:flex-col">
            <span className={`mt-1 block font-bold uppercase text-[#004C50] ${v.label}`}>
              {item.label}
            </span>
            {withSubline && (
              <span className={`block leading-tight text-secondary ${v.subline}`}>
                {item.subline}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CashGameMetaBar;
